import { useCallback, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { resolveImageSource } from "../../utils/resolveImageSource";
import { aiBackgroundSingle, shadowSingle, backgroundSearch } from "../../services/api/tools/studio_features";
import { upscaleSingle } from "../../services/api/tools/upscale";
import { outpaintSingle } from "../../services/api/tools/outpaint";
import { passportSuggestCrop, passportGenerate } from "../../services/api/tools/passport_photo";
import logger from "../../utils/logger";

/**
 * Preset knobs for POST /api/shadow/single (PhotoStudio universal fields).
 */
export const SHADOW_STYLE_PARAMS = {
  soft: { style: "soft", direction: "right", intensity: "medium" },
  hard: { style: "hard", direction: "right", intensity: "strong" },
  floating: { style: "floating", direction: "front", intensity: "medium" },
};

/**
 * Backend integration for ai-background, shadow, background-search (uses current editor image URL).
 * @param {string|null} imageUrl
 * @param {() => object} [getLineage] - returns { parentCreationId, transparentSourceId, rootCreationId }
 */
export function useStudioFeatures(imageUrl, getLineage) {
  const { getToken } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const getLineageRef = useRef(getLineage);
  getLineageRef.current = getLineage;

  const resolveLineage = () => {
    try {
      return getLineageRef.current?.() ?? {};
    } catch {
      return {};
    }
  };

  const sourceIdsFromLineage = () => {
    const lineage = resolveLineage();
    return {
      creationId: lineage.parentCreationId ?? lineage.parent_creation_id ?? null,
      rootCreationId: lineage.rootCreationId ?? lineage.root_creation_id ?? null,
    };
  };

  const runAiBackgroundGenerate = useCallback(
    async ({
      prompt = null,
      outputFormat = "png",
      backgroundGuidanceImage = null,
      backgroundGuidanceImageUrl = null,
      backgroundNegativePrompt = null,
    } = {}) => {
      setBusy(true);
      setError(null);
      try {
        const token = await getToken();
        if (!token) throw new Error("Sign in required.");
        if (
          !prompt?.trim() &&
          !backgroundGuidanceImage &&
          !backgroundGuidanceImageUrl?.trim()
        ) {
          throw new Error("Enter a prompt or select a background reference.");
        }
        const { file, image_url } = await resolveImageSource({
          sourceUrl: imageUrl,
          filename: "subject.png",
          token,
          ...sourceIdsFromLineage(),
          missingError:
            "No image loaded. Remove background first, then try again.",
          unsupportedError: "Unsupported image URL for AI background.",
        });
        const data = await aiBackgroundSingle(token, file, {
          ...(prompt?.trim() ? { background_prompt: prompt.trim() } : {}),
          output_format: outputFormat,
          ...(image_url ? { image_url } : {}),
          ...(backgroundGuidanceImage
            ? { background_guidance_image: backgroundGuidanceImage }
            : {}),
          ...(backgroundGuidanceImageUrl?.trim()
            ? {
                background_guidance_image_url:
                  backgroundGuidanceImageUrl.trim(),
              }
            : {}),
          ...(backgroundNegativePrompt
            ? { background_negative_prompt: backgroundNegativePrompt }
            : {}),
          lineage: resolveLineage(),
        });
        if (!data.success && !data.output_url) {
          throw new Error(data.message || data.detail || "AI background failed.");
        }
        return data;
      } catch (e) {
        const msg = e.response?.data?.detail || e.message || "AI background failed";
        logger.error("runAiBackgroundGenerate", e);
        setError(msg);
        throw new Error(msg);
      } finally {
        setBusy(false);
      }
    },
    [getToken, imageUrl]
  );

  const runShadowApply = useCallback(
    async (styleId = "soft") => {
      setBusy(true);
      setError(null);
      try {
        const token = await getToken();
        if (!token) throw new Error("Sign in required.");
        const preset = SHADOW_STYLE_PARAMS[styleId] || SHADOW_STYLE_PARAMS.soft;
        const { file, image_url } = await resolveImageSource({
          sourceUrl: imageUrl,
          filename: "subject.png",
          token,
          ...sourceIdsFromLineage(),
          missingError:
            "No image loaded. Remove background first, then try again.",
          unsupportedError:
            "Unsupported image URL for shadow. Reload the image after background removal.",
        });
        const data = await shadowSingle(token, file, {
          ...preset,
          ...(image_url ? { image_url } : {}),
          lineage: resolveLineage(),
        });
        if (!data.success && !data.shadow_url) {
          throw new Error(data.message || data.detail || "Shadow failed.");
        }
        return data;
      } catch (e) {
        const msg = e.response?.data?.detail || e.message || "Shadow failed";
        logger.error("runShadowApply", e);
        setError(msg);
        throw new Error(msg);
      } finally {
        setBusy(false);
      }
    },
    [getToken, imageUrl]
  );

  const runBackgroundSearch = useCallback(
    async (query, options = {}) => {
      setBusy(true);
      setError(null);
      try {
        const token = await getToken();
        if (!token) throw new Error("Sign in required.");
        const data = await backgroundSearch(token, { query, ...options });
        if (!data.success) {
          throw new Error(data.message || data.detail || "Search failed.");
        }
        return data.result || {};
      } catch (e) {
        const msg = e.response?.data?.detail || e.message || "Search failed";
        logger.error("runBackgroundSearch", e);
        setError(msg);
        throw new Error(msg);
      } finally {
        setBusy(false);
      }
    },
    [getToken]
  );

  /**
   * Resolve the current editor `imageUrl` into either a File (for blob:/data:)
   * or an `image_url` string (for http(s)://). Throws when imageUrl is missing
   * or unsupported.
   */
  const resolveEditorSource = useCallback(
    async (filename = "subject.png") => {
      const token = await getToken();
      return resolveImageSource({
        sourceUrl: imageUrl,
        filename,
        token,
        ...sourceIdsFromLineage(),
        missingError:
          "No image loaded. Remove background first, then try again.",
      });
    },
    [getToken, imageUrl]
  );

  const runUpscale = useCallback(
    async (opts = {}) => {
      setBusy(true);
      setError(null);
      try {
        const token = await getToken();
        if (!token) throw new Error("Sign in required.");
        const { file, image_url } = await resolveEditorSource("upscale-input.png");
        const data = await upscaleSingle(token, file, {
          ...opts,
          ...(image_url ? { image_url } : {}),
          lineage: resolveLineage(),
        });
        if (!data.success && !data.output_url) {
          throw new Error(data.message || data.detail || "Upscale failed.");
        }
        return data;
      } catch (e) {
        const msg = e.response?.data?.detail || e.message || "Upscale failed";
        logger.error("runUpscale", e);
        setError(msg);
        throw new Error(msg);
      } finally {
        setBusy(false);
      }
    },
    [getToken, resolveEditorSource]
  );

  const runOutpaint = useCallback(
    async (opts = {}) => {
      setBusy(true);
      setError(null);
      try {
        const token = await getToken();
        if (!token) throw new Error("Sign in required.");

        const overrideFile = opts.file instanceof File ? opts.file : null;
        const overrideUrl =
          opts.image_url && String(opts.image_url).trim()
            ? String(opts.image_url).trim()
            : null;

        let file = overrideFile;
        let image_url = overrideUrl;
        if (!file && !image_url) {
          const resolved = await resolveEditorSource("outpaint-input.png");
          file = resolved.file;
          image_url = resolved.image_url;
        }

        const {
          file: _omitFile,
          image_url: _omitUrl,
          lineage: _omitLineage,
          ...apiOpts
        } = opts;

        const data = await outpaintSingle(token, file, {
          ...apiOpts,
          ...(image_url ? { image_url } : {}),
          lineage: resolveLineage(),
        });
        if (!data.success && !data.output_url) {
          throw new Error(data.message || data.detail || "Outpaint failed.");
        }
        return data;
      } catch (e) {
        const msg = e.response?.data?.detail || e.message || "Outpaint failed";
        logger.error("runOutpaint", e);
        setError(msg);
        throw new Error(msg);
      } finally {
        setBusy(false);
      }
    },
    [getToken, resolveEditorSource]
  );

  const runPassportSuggestCrop = useCallback(
    async (opts = {}) => {
      setBusy(true);
      setError(null);
      try {
        const token = await getToken();
        if (!token) throw new Error("Sign in required.");
        const { file, image_url } = await resolveEditorSource("passport-input.png");
        const data = await passportSuggestCrop(token, file, {
          ...opts,
          ...(image_url ? { image_url } : {}),
        });
        if (!data.success) {
          throw new Error(data.message || data.detail || "Passport suggest-crop failed.");
        }
        return data.result || {};
      } catch (e) {
        const msg = e.response?.data?.detail || e.message || "Passport suggest-crop failed";
        logger.error("runPassportSuggestCrop", e);
        setError(msg);
        throw new Error(msg);
      } finally {
        setBusy(false);
      }
    },
    [getToken, resolveEditorSource]
  );

  const runPassportGenerate = useCallback(
    async (opts = {}) => {
      setBusy(true);
      setError(null);
      try {
        const token = await getToken();
        if (!token) throw new Error("Sign in required.");
        const { file, image_url } = await resolveEditorSource("passport-input.png");
        const data = await passportGenerate(token, file, {
          ...opts,
          ...(image_url ? { image_url } : {}),
          lineage: resolveLineage(),
        });
        if (!data.success && !data.output_url) {
          throw new Error(data.message || data.detail || "Passport generate failed.");
        }
        return data;
      } catch (e) {
        const msg = e.response?.data?.detail || e.message || "Passport generate failed";
        logger.error("runPassportGenerate", e);
        setError(msg);
        throw new Error(msg);
      } finally {
        setBusy(false);
      }
    },
    [getToken, resolveEditorSource]
  );

  return {
    busy,
    error,
    setError,
    runAiBackgroundGenerate,
    runShadowApply,
    runBackgroundSearch,
    runUpscale,
    runOutpaint,
    runPassportSuggestCrop,
    runPassportGenerate,
  };
}
